import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { BUSINESS_DOC_EVENT } from '../flow/businessDocument';
import { COMPANY_EVENT } from '../flow/company';
import { BusinessDocumentStep } from '../flow/BusinessDocumentStep';
import { CaptureGuide } from '../flow/CaptureGuide';
import { ConsentStep, partnerNameFromSnapshot } from '../flow/ConsentStep';
import { DetailsForm } from '../flow/DetailsForm';
import { DipChipStep } from '../flow/DipChipStep';
import { DocumentChipStep } from '../flow/DocumentChipStep';
import { DocumentChoice } from '../flow/DocumentChoice';
import { DOCUMENTS_EVENT } from '../flow/DocumentGroupsStep';
import { DOCUMENT_CHIP_UNAVAILABLE_EVENT } from '../flow/documentChip';
import { documentsFor, eventFor, THAILAND, type DocumentKind } from '../flow/documents';
import { PreviewCamera } from '../preview/PreviewCamera';
import type { PositionOutcome } from '../flow/geolocation';
import { KEY_PEOPLE_DONE_EVENT, KEY_PEOPLE_EVENT } from '../flow/keyPeople';
import { PHONE_VERIFIED_EVENT, PhoneStep, type PhoneOtp } from '../flow/PhoneStep';
import {
  CONSENT_EVENT,
  DETAILS_EVENT,
  LIVENESS_EVENT,
  QUESTIONNAIRE_EVENT,
  screenFor,
  type Screen,
} from '../flow/screen';
import { SIGNATURE_DECLINED_EVENT, SIGNATURE_SUBMITTED_EVENT } from '../flow/SignatureStep';
import {
  BlockedScreen,
  CheckingScreen,
  ReviewScreen,
  StepHeading,
  TerminalScreen,
} from '../flow/StatusScreens';
import { stepFor } from '../flow/step';
import type { CollectionFlowSnapshot } from '../flow/types';
import {
  locateRecorded,
  parseHighlightMessage,
  parseStepMessage,
  type PreviewHighlightMessage,
  type PreviewScenario,
  type PreviewStepMessage,
} from '../preview';
import { fromParent, postToParent } from '../preview/parent';

const NOOP = () => {};

/** The event each KYB stand-in fires to move the walk on. */
const KYB_EVENTS = {
  company: COMPANY_EVENT,
  keyPeople: KEY_PEOPLE_EVENT,
  awaitingUsers: KEY_PEOPLE_DONE_EVENT,
  documentGroups: DOCUMENTS_EVENT,
  questionnaire: QUESTIONNAIRE_EVENT,
} as const;

/** Fires an applicant event: on a case it is POSTed, here it is reported. */
type Act = (event: string) => void;

/**
 * Screens that take no input. A run of them is ONE screen to the applicant —
 * FlowRunner mounts the same element for consecutive checks — so they are
 * keyed by kind, not state, and a walk through seven reports does not flash.
 */
const WAIT_SCREENS: ReadonlySet<Screen['kind']> = new Set([
  'terminal',
  'blocked',
  'checking',
  'review',
]);

/**
 * The position a preview "gives": it never leaves the frame — the console is
 * told the event's name and nothing else — and asking the browser would fail
 * here anyway, in a frame no permission was granted to.
 */
const NO_POSITION: PositionOutcome = { ok: true, fix: { latitude: 0, longitude: 0, accuracy: 0 } };
const locateNowhere = () => Promise.resolve(NO_POSITION);

/** As the server's `mask_msisdn`, which is what the applicant would read. */
function maskPhone(phone: string): string {
  const s = phone.trim();
  return s.length < 8 ? '***' : `${s.slice(0, 5)}****${s.slice(-3)}`;
}

/** OTP that texts nobody: any number gets a code, and any code is right. */
function previewOtp(act: Act): PhoneOtp {
  let number = '';
  const sent = () =>
    Promise.resolve({ ref_code: 'DEMO', phone_masked: maskPhone(number), status: 'sent' });
  return {
    send: (phone) => {
      number = phone;
      return sent();
    },
    resend: sent,
    verify: () => Promise.resolve({ verified: true, status: 'verified' }),
    advance: () => Promise.resolve(act(PHONE_VERIFIED_EVENT)),
  };
}

/**
 * `/preview/flow` — one step of a DRAFT workflow, framed by the console's
 * workflow builder. The console pushes whichever state the operator picked (or
 * the agent just wrote); this classifies it with the live flow's own `stepFor`
 * and `screenFor` and mounts the live flow's own components.
 *
 * It can be clicked through: an applicant's act here reports the EVENT it
 * fires on a case (`trustsy:preview-event`) and the console answers with the
 * step that event leads to. Only the name crosses — nothing typed, picked or
 * drawn leaves the frame, and no step POSTs, uploads, texts or asks for a
 * position. What cannot run without a case is stood in for: a capture step
 * shows its guide and its button takes the photo as given (the camera would
 * prompt the operator), and the signature step shows its heading (its document
 * is generated per case).
 */
export function PreviewFlowScreen() {
  const { t } = useTranslation();
  const [message, setMessage] = useState<PreviewStepMessage | null>(null);
  const [press, setPress] = useState<PreviewHighlightMessage | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!fromParent(event)) return;
      const highlight = parseHighlightMessage(event.data);
      if (highlight) {
        setPress(highlight);
        return;
      }
      const next = parseStepMessage(event.data);
      if (!next) return;
      setMessage(next);
      // The global instance, as in PreviewScreen: the hook's changes identity
      // per language and would re-subscribe on every switch.
      if (next.language && next.language !== i18n.language) {
        void i18n.changeLanguage(next.language);
      }
    };
    window.addEventListener('message', onMessage);
    // After subscribing, so the console's first reply cannot arrive unheard.
    postToParent({ type: 'trustsy:preview-ready' });
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // The shell's EN/ไทย toggle is the applicant's own and stays live here, so a
  // switch can start in the frame; report every one, and the console's language
  // control follows rather than naming a language this frame is not showing.
  useEffect(() => {
    const onLanguage = (language: string) => {
      postToParent({ type: 'trustsy:preview-language', language });
    };
    i18n.on('languageChanged', onLanguage);
    return () => i18n.off('languageChanged', onLanguage);
  }, []);

  const snapshot = useMemo<CollectionFlowSnapshot | null>(
    () =>
      message && {
        id: 'preview',
        state: message.step.state,
        status: message.step.final ? 'completed' : 'active',
        tags: message.step.tags,
        context: null,
        available_events: message.step.events,
        done: message.step.final,
        branding: message.branding,
      },
    [message],
  );
  const screen = useMemo(() => (snapshot ? screenFor(stepFor(snapshot)) : null), [snapshot]);
  const state = snapshot?.state ?? null;
  const kind = screen?.kind ?? null;
  const scenarioKey = message?.scenario
    ? `${message.scenario.country}/${message.scenario.document ?? ''}`
    : '';

  // Say what is on show, so the console can walk past an automated check.
  useEffect(() => {
    if (state !== null && kind !== null) {
      postToParent({ type: 'trustsy:preview-screen', state, screen: kind });
    }
  }, [state, kind]);

  const act = useCallback<Act>(
    (event) => {
      if (state !== null) postToParent({ type: 'trustsy:preview-event', state, event });
    },
    [state],
  );

  if (!snapshot || !screen) {
    return (
      <main className="screen">
        <div className="loading">
          <span className="spinner" aria-hidden="true" />
          <p>{t('flow.loading', { defaultValue: 'Loading…' })}</p>
        </div>
      </main>
    );
  }

  return (
    <div className="preview-flow" data-testid="preview-flow">
      <p className="preview-ribbon" role="note">
        {t('preview.flowRibbon')}
      </p>
      <PreviewStep
        // A new scenario restarts the step, so its document choice follows it.
        key={`${WAIT_SCREENS.has(screen.kind) ? `wait:${screen.kind}` : `step:${snapshot.state}`}:${scenarioKey}`}
        act={act}
        scenario={message?.scenario}
        screen={screen}
        snapshot={snapshot}
      />
      <PressMarker press={press} state={snapshot.state} />
    </div>
  );
}

/**
 * A replayed press: a ring around the control it landed on, drawn after the
 * step it belongs to has rendered. Keyed by the press's time, so the ring
 * animates again when the same control is pressed twice. Tells the console
 * whether the press could be placed, so it can say so instead of the ring.
 */
function PressMarker({
  press,
  state,
}: {
  press: PreviewHighlightMessage | null;
  state: string | null;
}) {
  // Which press was placed, and where: a later press (or a clear) simply no
  // longer matches, so nothing has to be taken down.
  const [placed, setPlaced] = useState<{ at: number; box: DOMRect } | null>(null);
  useEffect(() => {
    if (!press?.xpath) return;
    const { at, xpath } = press;
    // After the step message's render, which arrives in the same tick.
    const frame = requestAnimationFrame(() => {
      const target = locateRecorded(xpath, document);
      if (target) {
        // A block body: Chrome's scrollIntoView now returns a Promise.
        target.scrollIntoView({ block: 'nearest' });
      }
      setPlaced(target ? { at, box: target.getBoundingClientRect() } : null);
      postToParent({ type: 'trustsy:preview-pressed', at, found: target !== null });
    });
    return () => cancelAnimationFrame(frame);
  }, [press, state]);

  if (!press?.xpath || placed?.at !== press.at) return null;
  const { box } = placed;
  return (
    <div
      aria-hidden="true"
      className="press-marker"
      data-testid="press-marker"
      key={press.at}
      style={{ height: box.height + 8, left: box.left - 4, top: box.top - 4, width: box.width + 8 }}
    />
  );
}

function PreviewStep({
  act,
  scenario,
  screen,
  snapshot,
}: {
  act: Act;
  scenario?: PreviewScenario;
  screen: Screen;
  snapshot: CollectionFlowSnapshot;
}) {
  const { t } = useTranslation();
  // Once per step (this is keyed by state): the code it "sent" outlives renders.
  const otp = useMemo(() => previewOtp(act), [act]);
  const { state } = snapshot;
  switch (screen.kind) {
    case 'terminal':
      return <TerminalScreen outcome={screen.outcome} />;
    case 'blocked':
      return <BlockedScreen />;
    case 'checking':
      return <CheckingScreen />;
    case 'review':
      return <ReviewScreen />;
    case 'consent':
      return (
        <Card>
          <ConsentStep
            branding={snapshot.branding}
            locate={locateNowhere}
            onAgree={() => act(CONSENT_EVENT)}
            partnerName={partnerNameFromSnapshot(snapshot)}
          />
        </Card>
      );
    case 'document':
      return <PreviewDocument act={act} offered={screen.offered} scenario={scenario} />;
    case 'documentBack':
      return (
        <GuideThenCamera
          document={screen.document}
          kind="document"
          onCapture={() => act(eventFor(screen.document, 'back'))}
        />
      );
    case 'liveness':
      return <GuideThenCamera kind="selfie" onCapture={() => act(LIVENESS_EVENT)} />;
    case 'businessDocument':
      return (
        <Card>
          <BusinessDocumentStep
            token=""
            onAdvance={NOOP}
            submit={() => Promise.resolve(act(BUSINESS_DOC_EVENT))}
          />
        </Card>
      );
    case 'phone':
      return (
        <Card>
          <PhoneStep token="" onAdvance={NOOP} otp={otp} />
        </Card>
      );
    case 'documentChip':
      return (
        <Card>
          <DocumentChipStep
            token=""
            onAdvance={NOOP}
            skip={() => Promise.resolve(act(DOCUMENT_CHIP_UNAVAILABLE_EVENT))}
          />
        </Card>
      );
    case 'dipChip':
      return (
        <Card>
          <DipChipStep />
        </Card>
      );
    case 'signature':
      return (
        <Card>
          {/* SignatureStep's own title — the ceremony has no state title. */}
          <h1 className="step-title">
            {t('flow.sign.title', { defaultValue: 'Review and sign this document' })}
          </h1>
          <p className="step-text">
            {t('preview.signature', {
              defaultValue: 'The document to sign appears here, generated for each applicant.',
            })}
          </p>
          <div className="actions">
            <button className="btn" type="button" onClick={() => act(SIGNATURE_SUBMITTED_EVENT)}>
              {t('flow.sign.sign', { defaultValue: 'Sign document' })}
            </button>
            {snapshot.available_events.includes(SIGNATURE_DECLINED_EVENT) ? (
              <button
                className="btn btn--ghost"
                type="button"
                onClick={() => act(SIGNATURE_DECLINED_EVENT)}
              >
                {t('flow.sign.decline', { defaultValue: 'Decline' })}
              </button>
            ) : null}
          </div>
        </Card>
      );
    // KYB steps are drawn from a view the SERVER builds from the step's
    // settings and its own defaults; rebuilding it here would be a second copy
    // to drift. So they stand in, like the signature: the heading, what goes
    // here, and the act that moves the walk on.
    case 'company':
    case 'keyPeople':
    case 'awaitingUsers':
    case 'documentGroups':
    case 'questionnaire':
      return (
        <Card>
          <StepHeading state={state} />
          <p className="step-text">{t(`preview.kyb.${screen.kind}`)}</p>
          <div className="actions">
            <button className="btn" type="button" onClick={() => act(KYB_EVENTS[screen.kind])}>
              {t('preview.continue')}
            </button>
          </div>
        </Card>
      );
    case 'details':
      return (
        <Card>
          <StepHeading state={state} />
          <DetailsForm busy={false} onSubmit={() => act(DETAILS_EVENT)} />
        </Card>
      );
    case 'actions':
      return (
        <Card>
          <StepHeading state={state} />
          <div className="actions">
            {screen.actions.map((action) => (
              <button className="btn" key={action} type="button" onClick={() => act(action)}>
                {t(`flow.event.${action}`, { defaultValue: action })}
              </button>
            ))}
          </div>
        </Card>
      );
  }
}

/**
 * The document front, as FlowRunner runs it: choose (when the step offers
 * more than one), read the guide, take the photo — where the choice IS the
 * event, so a passport skips the back.
 */
function PreviewDocument({
  act,
  offered,
  scenario,
}: {
  act: Act;
  offered: DocumentKind[];
  scenario?: PreviewScenario;
}) {
  const [country, setCountry] = useState(scenario?.country ?? THAILAND);
  // The operator's branch is taken when the step offers it in that country;
  // otherwise the applicant's choice is shown, as on a case.
  const branch =
    scenario?.document &&
    offered.includes(scenario.document) &&
    documentsFor(scenario.country).includes(scenario.document)
      ? scenario.document
      : null;
  const [chosen, setChosen] = useState<DocumentKind | null>(
    branch ?? (offered.length === 1 ? (offered[0] ?? null) : null),
  );
  const [aiming, setAiming] = useState(false);
  if (!chosen) {
    return (
      <Card>
        <DocumentChoice
          offered={offered}
          country={country}
          onCountryChange={setCountry}
          onChoose={setChosen}
        />
      </Card>
    );
  }
  if (aiming) {
    return (
      <PreviewCamera
        document={chosen}
        kind="document"
        onCapture={() => act(eventFor(chosen, 'front'))}
      />
    );
  }
  return (
    <Card>
      <CaptureGuide
        kind="document"
        document={chosen}
        onStart={() => setAiming(true)}
        onChangeDocument={offered.length > 1 ? () => setChosen(null) : undefined}
      />
    </Card>
  );
}

/**
 * A capture step as a case runs it — the guide, then the camera — with the
 * camera drawn (`PreviewCamera`): the shutter fires the step's event.
 */
function GuideThenCamera({
  document,
  kind,
  onCapture,
}: {
  document?: DocumentKind;
  kind: 'document' | 'selfie';
  onCapture: () => void;
}) {
  const [aiming, setAiming] = useState(false);
  if (aiming) return <PreviewCamera document={document} kind={kind} onCapture={onCapture} />;
  return (
    <Card>
      {kind === 'selfie' ? (
        <CaptureGuide kind="selfie" onStart={() => setAiming(true)} />
      ) : (
        <CaptureGuide
          kind="document"
          document={document ?? 'thai_id'}
          onStart={() => setAiming(true)}
        />
      )}
    </Card>
  );
}

function Card({ children }: { children: ReactNode }) {
  return (
    <main className="screen">
      <div className="card enter">{children}</div>
    </main>
  );
}
